import { createForm, useCreateFormStore } from '@components/form';
import { IconEdit } from '@components/icons/edit';
import { IconFormIndicatorWarning } from '@components/icons/form-indicator-warning';
import { IconIntegration } from '@components/icons/integration';
import { InfoBox } from '@components/info-box';
import { Sidebar, hideSidebar, useSidebar } from '@components/sidebar';
import { useScopedTranslate } from '@locale/translate';

import { useCreateIntegration, useEditIntegration } from '../integrations-hooks';
import type { Integration, IntegrationBase } from '../integrations-types';

const Form = createForm<IntegrationBase>();

export const useIntegrationAddEditSidebar = () =>
  useSidebar(IntegrationAddEditSidebar, { variant: 'REGULAR' });

type IntegrationAddEditSidebarProps = {
  integration?: Integration;
  onCreated?: (integration: Integration) => void;
};

const IntegrationAddEditSidebar = ({ integration, onCreated }: IntegrationAddEditSidebarProps) => {
  const { t } = useScopedTranslate(locale);
  const createMutation = useCreateIntegration();
  const editMutation = useEditIntegration();
  const formStore = useCreateFormStore();
  const isDirty = formStore.useIsDirty();

  const isEdit = Boolean(integration);
  const initialBaseUrl = integration?.baseUrl ?? '';
  const currentBaseUrl = ((formStore.useFieldValue('baseUrl') as string) ?? '').trim();
  const showBaseUrlWarning =
    isEdit &&
    Boolean(initialBaseUrl) &&
    !currentBaseUrl &&
    ((integration?.endpoints?.length ?? 0) > 0 || (integration?.webhooks?.length ?? 0) > 0);

  const handleSubmit = async (data: IntegrationBase) => {
    if (isEdit) {
      await editMutation.mutateAsync({ integrationId: integration!.shortId, data });
      hideSidebar();
    } else {
      const response = await createMutation.mutateAsync(data);
      hideSidebar();
      onCreated?.(response);
    }
  };

  return (
    <Sidebar>
      <Sidebar.SimpleHeader
        icon={isEdit ? IconEdit : IconIntegration}
        iconColor="elementBlue100"
        title={isEdit ? t('editIntegration') : t('addIntegration')}
        color="textPrimary"
      />
      <Sidebar.Content>
        <Form onSubmit={handleSubmit} store={formStore}>
          <Form.LogoField
            // @ts-ignore: This fails because logo is optional in the type
            id="logo"
            label={t('integrationLogo')}
            defaultImage={integration?.logo}
            dataTestid="upload-image"
          />
          <Form.TextField
            id="name"
            label={t('name')}
            placeholder={t('namePlaceholder')}
            validators={['required']}
            defaultValue={integration?.name}
          />
          <Form.Textarea
            id="description"
            label={t('description')}
            placeholder={t('descriptionPlaceholder')}
            defaultValue={integration?.description}
          />
          <Form.UrlField
            id="baseUrl"
            label={t('baseUrl')}
            placeholder={t('baseUrlPlaceholder')}
            defaultValue={integration?.baseUrl}
            helpText={t('baseUrlHelpText')}
          />
          {showBaseUrlWarning && (
            <InfoBox
              variant="orange"
              icon={<IconFormIndicatorWarning />}
              text={t('baseUrlClearedWarning')}
            />
          )}
          <Sidebar.Footer>
            <Form.Actions>
              <Form.Reset
                text={t('core.cancel')}
                variant="secondary"
                onClick={hideSidebar}
                isDisabled={createMutation.isLoading || editMutation.isLoading}
              />
              <Form.Submit
                text={isEdit ? t('updateIntegration') : t('addIntegration')}
                variant="primary-blue"
                isDisabled={!isDirty || createMutation.isLoading || editMutation.isLoading}
              />
            </Form.Actions>
          </Sidebar.Footer>
        </Form>
      </Sidebar.Content>
    </Sidebar>
  );
};

const locale = {
  en: {
    addIntegration: 'Add custom integration',
    editIntegration: 'Edit integration',
    updateIntegration: 'Update integration',
    name: 'Name',
    namePlaceholder: 'Integration name',
    description: 'Description',
    descriptionPlaceholder: 'Short description',
    url: 'URL',
    submit: 'Submit',
    exampleRequest: 'Example request',
    integrationLogo: 'Integration logo',
    baseUrl: 'Base URL',
    baseUrlPlaceholder: 'Base URL for endpoints',
    baseUrlHelpText:
      'If all the endpoints have the same base URL, you can enter it here. Otherwise, leave it blank.',
    baseUrlClearedWarning:
      'When clearing the base URL, you need to update the endpoint and webhook urls for them to work correctly.',
  },
};
